import { inject, Injectable } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { Cachier } from '@lib.core/store/cachier';
import { AspectCollection } from '@lib.core/entities/indicator/indicator';
import { ProfileOverview } from '@lib.core/entities/profile/profile-overview';
import { ProfileAspectCollection } from '@lib.core/entities/profile/profile-aspect-collection';
import { ProfileResource } from '@lib.resources/profile/resource';
import { ProfileApiService } from '@lib.resources/profile/profile-api.service';

@Injectable({
  providedIn: 'root'
})
export class ProfileStore implements ProfileResource {

  private readonly cache = inject(Cachier);
  private readonly profileApiService = inject(ProfileApiService);
  private readonly profileOverviewKey = 'profile-overview';
  private readonly profilesOverviewKey = 'profiles-overview';
  private readonly profileAspectCollectionKey = 'aspect-collection';
  private readonly profileOverviewByNameIdKey = 'profile-overview-by-name-id';
  private readonly profileAspectsByNameIdKey = 'profile-aspects-by-name-id';
  private readonly keys = new Set<string>();

  getProfilesOverview(clientId: string, locationId: string, isFeatured = false): Observable<ProfileOverview[]> {
    const key = `${this.profilesOverviewKey}-${clientId}-${locationId}-${isFeatured}`;
    this.keys.add(key);
    return this.cache.get(key, () => this.profileApiService.getProfilesOverview(clientId, locationId, isFeatured))
      .pipe(filter((po: ProfileOverview[] | null | undefined) => po != null));
  }

  getProfileOverview(profileHashKey: string, clientId: string): Observable<ProfileOverview> {
    const key = `${this.profileOverviewKey}-${clientId}-${profileHashKey}`;
    this.keys.add(key);
    return this.cache.get(key, () => this.profileApiService.getProfileOverview(profileHashKey, clientId))
      .pipe(filter((po: ProfileOverview | null | undefined) => po != null));
  }

  getProfileAspectCollection(profileHashKey: string, clientId: string): Observable<AspectCollection> {
    const key = `${this.profileAspectCollectionKey}-${clientId}-${profileHashKey}`;
    this.keys.add(key);
    return this.cache.get<AspectCollection>(key, () => this.profileApiService.getProfileAspectCollection(profileHashKey, clientId))
      .pipe(filter((co: AspectCollection | null | undefined) => co != null));
  }

  getProfileOverviewByNameId(profileNameId: string, locationId: string, clientId: string): Observable<ProfileOverview> {
    const key = `${this.profileOverviewByNameIdKey}-${clientId}-${locationId}-${profileNameId}`;
    this.keys.add(key);
    return this.cache.get(key, () => this.profileApiService.getProfileOverviewByNameId(profileNameId, locationId, clientId))
      .pipe(filter((po: ProfileOverview | null | undefined) => po != null));
  }

  getProfileAspectsByNameId(profileNameId: string, locationId: string, clientId: string): Observable<ProfileAspectCollection> {
    const key = `${this.profileAspectsByNameIdKey}-${clientId}-${locationId}-${profileNameId}`;
    this.keys.add(key);
    return this.cache.get<ProfileAspectCollection>(key, () => this.profileApiService.getProfileAspectsByNameId(profileNameId, locationId, clientId))
      .pipe(filter((co: ProfileAspectCollection | null | undefined) => co != null));
  }

  destroyAll() {
    this.keys.forEach((key: string) => this.cache.purge(key));
    this.keys.clear();
  }
}
